import type { ComponentProps } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "ttag";

import { deleteWeightEntry } from "@/app/api/legacy-api";
import { LogoLoader } from "@/shared/components";
import type { WeightEntry } from "@/shared/domains";
import { WEIGHT_UNIT } from "@/shared/models";
import { useSettingsStore } from "@/shared/stores";
import {
  Button,
  Card,
  Dialog,
  errorToast,
  GraphIcon,
  useToastStore,
} from "@/shared/ui";
import { formatBackendDate } from "@/shared/utils";

interface DeleteWeightEntryProps extends ComponentProps<typeof Dialog.Root> {
  weightEntry: WeightEntry;
}

export const DeleteWeightEntry = ({
  weightEntry,
  onOpenChange,
  ...props
}: DeleteWeightEntryProps) => {
  const { pushToast } = useToastStore();
  const unitSystem = useSettingsStore((state) => state.unitSystem);

  const queryClient = useQueryClient();
  const { mutate: deleteWeightEntryMutation, isPending } = useMutation({
    mutationFn: deleteWeightEntry.mutation,
    onSuccess: () => {
      onOpenChange?.(false);
      deleteWeightEntry.invalidates(queryClient);
      void pushToast({
        type: "success",
        title: t`Success`,
        message: t`Weight entry successfully deleted!`,
      });
    },
    onError: (err) => {
      errorToast(err);
    },
  });

  return (
    <Dialog.Root onOpenChange={onOpenChange} {...props}>
      <Dialog.Content>
        <Dialog.Header icon={GraphIcon}>
          <Dialog.Title>{t`Delete Entry`}</Dialog.Title>
          <Dialog.Description>{t`Are you sure you want to delete the next entry?`}</Dialog.Description>
        </Dialog.Header>

        <Card.Root>
          <Card.Description className="text-base">
            {t`Weight: `}
            <strong className="text-neutral-950">
              {weightEntry.weight} {WEIGHT_UNIT[unitSystem]}
            </strong>
          </Card.Description>
          <Card.Description className="text-base">
            {t`Date: `}
            <strong className="text-neutral-950">
              {formatBackendDate(weightEntry.date)}
            </strong>
          </Card.Description>
          <div className="flex gap-1">
            <Card.Description className="text-base">
              {t`Note: `}
            </Card.Description>
            <strong className="text-neutral-950">
              {weightEntry.note ?? "-"}
            </strong>
          </div>
        </Card.Root>

        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button variant="secondary" size="lg">
              {t`Cancel`}
            </Button>
          </Dialog.Close>

          <Button
            disabled={isPending}
            size="lg"
            onClick={() => deleteWeightEntryMutation(weightEntry.id)}
          >
            {t`Delete entry`}
          </Button>
        </Dialog.Footer>

        <LogoLoader show={isPending} fullscreen />
      </Dialog.Content>
    </Dialog.Root>
  );
};
